import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

/**
 * React Query hooks for the ransomware-groups manual triage UI.
 *
 * Server contract: `apps/api/src/routes/admin/ransomware-groups.routes.ts`.
 * Mirrors the Phase C C3 CPE-vendor-mappings hook shape (see
 * `useCpeMappings.ts`).
 */

// ─── Server-shape types ──────────────────────────────────────────────────────

export interface RansomwareGroupsStats {
  totalGroups: number;
  mappedGroups: number;
  unmappedGroups: number;
  /** Always 0 when totalGroups=0; rounded to 2dp otherwise. */
  percentMapped: number;
}

export interface UnmappedGroupRow {
  groupId: string;
  name: string;
  aliases: string[];
  aliasCount: number;
  claimCount: number;
  lastSeenAt: string | null;
  sampleClaimUrl: string | null;
  sampleVictimName: string | null;
}

export interface UnmappedGroupsPage {
  page: number;
  pageSize: number;
  rows: UnmappedGroupRow[];
}

export interface RansomwareGroupsTriageResponse {
  stats: RansomwareGroupsStats;
  unmapped: UnmappedGroupsPage;
}

// ─── Mutation payloads ───────────────────────────────────────────────────────

export interface MapGroupPayload {
  groupId: string;
  threatActorId: string;
}

export interface CreateActorPayload {
  groupId: string;
  actorName: string;
  mitreId?: string;
  mispUuid?: string;
  malpediaId?: string;
  description?: string;
  /**
   * KAN-223 U7 C14 — REQUIRED, not optional.
   *
   * Creating an actor here writes an identity-support evidence row, which is the
   * provenance record for a human-asserted canonical identity. The server rejects
   * a create without a reason (`z.string().min(1).max(500)`), so omitting it here
   * is a 400, not a silent default.
   */
  reason: string;
  approvalReference?: string;
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

const KEY = "admin-ransomware-groups";

export function useRansomwareGroupsTriage(page: number, pageSize: number) {
  return useQuery({
    queryKey: [KEY, page, pageSize],
    queryFn: () =>
      api.get<RansomwareGroupsTriageResponse>(
        `/admin/ransomware-groups?page=${page}&pageSize=${pageSize}`,
      ),
  });
}

export function useMapGroupToActor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: MapGroupPayload) =>
      api.post<{
        ok: true;
        mapping: {
          groupId: string;
          groupName: string;
          threatActorId: string;
          actorName: string;
        };
      }>("/admin/ransomware-groups/map", payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
      qc.invalidateQueries({ queryKey: ["threat-actors"] });
    },
  });
}

export function useCreateActorForGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateActorPayload) =>
      api.post<{
        ok: true;
        threatActorId: string;
        mapping: {
          groupId: string;
          groupName: string;
          threatActorId: string;
          actorName: string;
        };
      }>("/admin/ransomware-groups/create", payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
      qc.invalidateQueries({ queryKey: ["threat-actors"] });
    },
  });
}
